import React, { useEffect, useState } from 'react'
import { getCurrentUser, signOut as amplifySignOut } from 'aws-amplify/auth'
import { Authenticator } from '@aws-amplify/ui-react'
import { useNavigate } from 'react-router-dom'
import '@aws-amplify/ui-react/styles.css'

interface User {
  username: string
  email?: string
  name?: string
}

interface AuthWrapperProps {
  children: (props: { user: User | null; signOut: () => void }) => React.ReactNode
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    checkAuthState()
  }, [])

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser()
      const userData = {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId,
        name: currentUser.username
      }
      setUser(userData)
      
      // If user is authenticated and we're on the root path, redirect to dashboard
      // Otherwise, stay on the current path (preserve deep links)
      if (window.location.pathname === '/') {
        navigate('/dashboard', { replace: true })
      }
    } catch (error) {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await amplifySignOut()
      setUser(null)
      navigate('/', { replace: true })
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div>Loading...</div>
      </div>
    )
  }

  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px'
      }}>
        <div style={{ maxWidth: '400px', width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <img 
              src="/logo.svg" 
              alt="Live Maps" 
              style={{ 
                width: '120px', 
                height: '120px', 
                marginBottom: '20px' 
              }} 
            />
            <h1 style={{ color: '#232f3e', marginBottom: '10px', fontSize: '2rem' }}>Live Maps</h1>
            <p style={{ color: '#666', margin: 0, fontSize: '1.1rem' }}>User Portal</p>
          </div>
          <Authenticator
            signUpAttributes={['email']}
            socialProviders={['google']}
          >
            {({ signOut, user: authUser }) => {
              // When authentication succeeds, update our state
              // The redirect will be handled by checkAuthState
              if (authUser && !user) {
                checkAuthState()
              }
              return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Authentication successful. Loading...</p>
                </div>
              )
            }}
          </Authenticator>
        </div>
      </div>
    )
  }

  return <>{children({ user, signOut: handleSignOut })}</>
}

export default AuthWrapper